import { generatePuzzle } from './generatePuzzle';
import type { DensityPreset, PuzzleData, TrainSeed } from './types';

export interface TrainPuzzleRequest {
  size: number;
  seed: TrainSeed;
  densityPreset: DensityPreset;
}

interface WorkerRequestMessage {
  id: number;
  request: TrainPuzzleRequest;
}

interface WorkerSuccessMessage {
  id: number;
  status: 'success';
  puzzle: PuzzleData;
}

interface WorkerErrorMessage {
  id: number;
  status: 'error';
  message: string;
}

type WorkerResponseMessage = WorkerSuccessMessage | WorkerErrorMessage;

const puzzleCache = new Map<string, Promise<PuzzleData>>();
const pendingRequests = new Map<
  number,
  {
    resolve: (puzzle: PuzzleData) => void;
    reject: (error: Error) => void;
  }
>();

let worker: Worker | null = null;
let nextRequestId = 1;

export function getTrainPuzzleCacheKey(request: TrainPuzzleRequest): string {
  return JSON.stringify([request.size, request.seed, request.densityPreset]);
}

export function clearTrainPuzzleCache(): void {
  puzzleCache.clear();
}

function resetWorker(error?: Error): void {
  if (error) {
    for (const pending of pendingRequests.values()) {
      pending.reject(error);
    }
    pendingRequests.clear();
  }

  worker?.terminate();
  worker = null;
}

function getWorker(): Worker | null {
  if (typeof Worker === 'undefined') return null;
  if (worker) return worker;

  worker = new Worker(new URL('./generatePuzzle.worker.ts', import.meta.url), {
    type: 'module',
  });

  worker.onmessage = (event: MessageEvent<WorkerResponseMessage>) => {
    const pending = pendingRequests.get(event.data.id);
    if (!pending) return;
    pendingRequests.delete(event.data.id);

    if (event.data.status === 'success') {
      pending.resolve(event.data.puzzle);
      return;
    }

    pending.reject(new Error(event.data.message));
  };

  worker.onerror = () => {
    resetWorker(new Error('Puzzle generation worker crashed.'));
  };

  return worker;
}

function generatePuzzleInWorker(
  request: TrainPuzzleRequest
): Promise<PuzzleData> {
  const currentWorker = getWorker();
  if (!currentWorker) {
    return Promise.resolve().then(() =>
      generatePuzzle(request.size, request.seed, request.densityPreset)
    );
  }

  return new Promise<PuzzleData>((resolve, reject) => {
    const id = nextRequestId++;
    pendingRequests.set(id, { resolve, reject });
    currentWorker.postMessage({ id, request } satisfies WorkerRequestMessage);
  });
}

export function generatePuzzleAsync(
  size: number,
  seed: TrainSeed,
  densityPreset: DensityPreset = 'normal'
): Promise<PuzzleData> {
  const request: TrainPuzzleRequest = { size, seed, densityPreset };
  const cacheKey = getTrainPuzzleCacheKey(request);
  const cached = puzzleCache.get(cacheKey);
  if (cached) return cached;

  const promise = generatePuzzleInWorker(request);
  puzzleCache.set(cacheKey, promise);
  promise.catch(() => {
    if (puzzleCache.get(cacheKey) === promise) {
      puzzleCache.delete(cacheKey);
    }
  });

  return promise;
}
